import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Search,
  Shield,
  ShieldCheck,
  Users,
  MapPin,
  Mail,
  UserPlus,
  KeyRound,
  User as UserIcon,
  Eye,
  EyeOff,
  Pencil,
  Ban,
  CheckCircle2,
  History,
} from "lucide-react";

import {
  crmGetMyAccess,
  crmListCities,
  crmListReps,
  crmCreateRepAccount,
  crmRemoveRep,
  crmSetRepAccess,
  crmUpdateRep,
  crmSetRepDisabled,
  crmListAccessAudit,
  crmListEditGrants,
  crmSetEditGrant,
  crmBulkSetEditGrants,
} from "@/lib/crm.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/crm/access")({
  component: AccessPage,
});

function initials(email: string) {
  const base = (email || "?").split("@")[0];
  return base.slice(0, 2).toUpperCase();
}

function scopeLabel(s: string) {
  if (s === "all") return "Full Access";
  if (s === "cities") return "City Scoped";
  return "Assigned Only";
}

function scopeTone(s: string) {
  if (s === "all") return { bg: "#19875422", fg: "#198754" };
  if (s === "cities") return { bg: "#0d6efd22", fg: "#0d6efd" };
  return { bg: "#6c757d22", fg: "#6c757d" };
}

function AccessPage() {
  const navigate = useNavigate();
  const getMe = useServerFn(crmGetMyAccess);
  const listReps = useServerFn(crmListReps);
  const listCities = useServerFn(crmListCities);
  const addRep = useServerFn(crmCreateRepAccount);
  const removeRep = useServerFn(crmRemoveRep);
  const setAccess = useServerFn(crmSetRepAccess);
  const updateRep = useServerFn(crmUpdateRep);
  const setDisabled = useServerFn(crmSetRepDisabled);

  const meQ = useQuery({ queryKey: ["crm", "me"], queryFn: () => getMe() });
  const citiesQ = useQuery({ queryKey: ["crm", "cities"], queryFn: () => listCities() });
  const repsQ = useQuery({
    queryKey: ["crm", "reps"],
    queryFn: () => listReps(),
    enabled: meQ.data?.is_admin === true,
  });

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [adding, setAdding] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<"reps" | "admins" | "audit">("reps");
  const listAudit = useServerFn(crmListAccessAudit);
  const listGrants = useServerFn(crmListEditGrants);
  const setGrant = useServerFn(crmSetEditGrant);
  const bulkGrant = useServerFn(crmBulkSetEditGrants);
  const isSuperAdmin = meQ.data?.is_super_admin === true;
  const [auditFrom, setAuditFrom] = useState<string>("");
  const [auditTo, setAuditTo] = useState<string>("");
  const auditFromIso = auditFrom ? new Date(auditFrom + "T00:00:00").toISOString() : null;
  const auditToIso = auditTo ? new Date(auditTo + "T23:59:59.999").toISOString() : null;
  const auditQ = useQuery({
    queryKey: ["crm", "access-audit", auditFromIso, auditToIso],
    queryFn: () => listAudit({ data: { from: auditFromIso, to: auditToIso, limit: 200 } }),
    enabled: isSuperAdmin && tab === "audit",
  });
  const exportAuditCsv = async () => {
    const res = await listAudit({
      data: { from: auditFromIso, to: auditToIso, limit: 5000 },
    });
    const rows = res.entries ?? [];
    const headers = ["created_at", "action", "actor_email", "actor_user_id", "target_email", "target_user_id", "notes"];
    const csv = [
      headers.join(","),
      ...rows.map((e: any) =>
        headers
          .map((h) => `"${String(e[h] ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `crm-access-audit-${auditFrom || "all"}-${auditTo || today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const grantsQ = useQuery({
    queryKey: ["crm", "edit-grants"],
    queryFn: () => listGrants(),
    enabled: meQ.data?.is_admin === true,
  });

  const cities = citiesQ.data?.cities ?? [];
  const reps = repsQ.data?.reps ?? [];

  const admins = useMemo(
    () => reps.filter((r: any) => r.is_admin),
    [reps],
  );
  const salesReps = useMemo(
    () => reps.filter((r: any) => !r.is_admin && r.is_sales_rep),
    [reps],
  );

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return salesReps;
    return salesReps.filter((r: any) => (r.email ?? "").toLowerCase().includes(t));
  }, [salesReps, q]);

  const stats = useMemo(() => {
    const all = salesReps.filter((r: any) => r.scope === "all").length;
    const city = salesReps.filter((r: any) => r.scope === "cities").length;
    const assigned = salesReps.length - all - city;
    return { total: salesReps.length, all, city, assigned };
  }, [salesReps]);


  if (meQ.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!meQ.data?.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div>
          <Shield className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Admins only.</p>
          <Button className="mt-4" onClick={() => navigate({ to: "/crm" })}>
            Back to CRM
          </Button>
        </div>
      </div>
    );
  }

  const submitAdd = async () => {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setAdding(true);
    try {
      await addRep({
        data: {
          email: email.trim(),
          password,
          display_name: displayName.trim() || undefined,
        },
      });
      toast.success("Sales rep created");
      setEmail("");
      setDisplayName("");
      setPassword("");
      setShowPw(false);
      setOpenAdd(false);
      repsQ.refetch();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#f6f7fb] pb-12">
      <header className="sticky top-0 z-20 rounded-2xl border border-border bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-7xl grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-3 sm:px-4 lg:px-6 py-3">
          <Link to="/crm" className="rounded-md p-2 hover:bg-muted" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate text-sm sm:text-base font-bold">Team Access</h1>
            <p className="hidden sm:block text-[11px] text-muted-foreground">
              Manage sales reps and their data visibility
            </p>
          </div>
          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-9 gap-1">
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Rep</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create CRM User</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground">
                Creates a sign-in for a salesperson. They can log in immediately with these credentials. Default visibility is "Assigned Only".
              </p>
              <div className="space-y-2.5 mt-1">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                    Display Name
                  </label>
                  <div className="relative">
                    <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Jane Doe"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                    Email (Username) *
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="rep@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                    Password *
                  </label>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-9"
                      autoComplete="new-password"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && email.trim() && password.length >= 8 && !adding) submitAdd();
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted"
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button
                onClick={submitAdd}
                disabled={adding || !email.trim() || password.length < 8}
                className="w-full"
              >
                {adding ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Create CRM User
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-4 px-3 py-4 sm:px-4 lg:px-6 lg:py-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
          <StatCard icon={Users} label="Total Reps" value={stats.total} tint="#0d6efd" />
          <StatCard icon={ShieldCheck} label="Full Access" value={stats.all} tint="#198754" />
          <StatCard icon={MapPin} label="City Scoped" value={stats.city} tint="#0d6efd" />
          <StatCard icon={Shield} label="Assigned Only" value={stats.assigned} tint="#6c757d" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 rounded-2xl border border-border bg-card p-1 shadow-[0_1px_0_rgba(13,17,38,0.04)]">
          <button
            type="button"
            onClick={() => setTab("reps")}
            className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all ${
              tab === "reps"
                ? "bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-sm"
                : "text-muted-foreground hover:bg-muted"
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            Sales Reps
            <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
              tab === "reps" ? "bg-white/25" : "bg-muted"
            }`}>
              {salesReps.length}
            </span>
          </button>
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => setTab("admins")}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all ${
                tab === "admins"
                  ? "bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Admins
              <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                tab === "admins" ? "bg-white/25" : "bg-muted"
              }`}>
                {admins.length}
              </span>
            </button>
          )}
          {isSuperAdmin && (
            <button
              type="button"
              onClick={() => setTab("audit")}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all ${
                tab === "audit"
                  ? "bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-sm"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <History className="h-3.5 w-3.5" />
              Audit
            </button>
          )}
        </div>


        {tab === "reps" && (
          <>
            {/* Search */}
            <div className="rounded-2xl border border-border bg-card p-3 shadow-[0_1px_0_rgba(13,17,38,0.04)]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by email…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-9 pl-9"
                />
              </div>
            </div>

            <section>
              <div className="mb-2 flex items-center justify-between px-1">
                <h2 className="text-sm font-semibold">
                  Sales Reps{" "}
                  <span className="text-muted-foreground font-normal">({filtered.length})</span>
                </h2>
              </div>

              {repsQ.isLoading ? (
                <div className="grid place-items-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                  <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/60" />
                  <p className="text-sm font-medium">No sales reps {q ? "match" : "yet"}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {q ? "Try a different search." : "Click 'Add Rep' to invite your first one."}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {filtered.map((r: any) => {
                    const repGrants = (grantsQ.data?.grants ?? [])
                      .filter((g: any) => g.admin_user_id === r.user_id)
                      .map((g: any) => g.rep_user_id as string);
                    const otherReps = salesReps.filter(
                      (s: any) => s.user_id !== r.user_id,
                    );
                    return (
                      <RepRow
                        key={r.user_id}
                        rep={r}
                        cities={cities}
                        otherReps={otherReps}
                        grantedRepIds={repGrants}
                        canManageGrants={isSuperAdmin}
                        onBulkGrant={async (repIds, mode) => {
                          await bulkGrant({
                            data: { admin_user_id: r.user_id, rep_user_ids: repIds, mode },
                          });
                          await grantsQ.refetch();
                        }}
                        onToggleGrant={async (repId, granted) => {
                          await setGrant({
                            data: { admin_user_id: r.user_id, rep_user_id: repId, granted },
                          });
                          await grantsQ.refetch();
                        }}
                        onChange={async (scope, ids, can_edit_others) => {
                          try {
                            await setAccess({
                              data: { user_id: r.user_id, scope, allowed_city_ids: ids, can_edit_others },
                            });
                            toast.success("Access updated");
                            repsQ.refetch();
                          } catch (e: any) {
                            toast.error(e?.message ?? "Failed");
                          }
                        }}
                        onRemove={async () => {
                          if (!confirm(`Remove ${r.email} from sales reps?`)) return;
                          try {
                            await removeRep({ data: { user_id: r.user_id } });
                            toast.success("Removed");
                            repsQ.refetch();
                          } catch (e: any) {
                            toast.error(e?.message ?? "Failed");
                          }
                        }}
                        onEdit={async (display_name, password) => {
                          try {
                            await updateRep({
                              data: {
                                user_id: r.user_id,
                                display_name: display_name || null,
                                password: password || null,
                              },
                            });
                            toast.success("Updated");
                            repsQ.refetch();
                          } catch (e: any) {
                            toast.error(e?.message ?? "Failed");
                          }
                        }}
                        onToggleDisabled={async (disabled) => {
                          try {
                            await setDisabled({ data: { user_id: r.user_id, disabled } });
                            toast.success(disabled ? "Access disabled" : "Access enabled");
                            repsQ.refetch();
                          } catch (e: any) {
                            toast.error(e?.message ?? "Failed");
                          }
                        }}
                      />
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}

        {tab === "admins" && isSuperAdmin && (
          <section>
            <div className="mb-2 flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                Admins{" "}
                <span className="text-muted-foreground font-normal">({admins.length})</span>
              </h2>
              <span className="text-[11px] text-muted-foreground">
                Auto Full Access
              </span>
            </div>
            {admins.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-muted-foreground/60" />
                <p className="text-sm font-medium">No admins</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Manage admin roles in the Site Admin panel.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {admins.map((a: any) => {
                  const adminGrants = (grantsQ.data?.grants ?? [])
                    .filter((g: any) => g.admin_user_id === a.user_id)
                    .map((g: any) => g.rep_user_id as string);
                  return (
                    <li
                      key={a.user_id}
                      className="rounded-2xl border border-border bg-card p-3 shadow-[0_1px_0_rgba(13,17,38,0.04)]"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
                          style={{ background: "linear-gradient(135deg,#198754,#3ec27a)" }}
                        >
                          {initials(a.email ?? "?")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 truncate text-sm font-semibold">
                              {a.email ?? a.user_id}
                            </div>
                            <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                              {a.is_super_admin ? "Super Admin" : "Admin"}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {a.is_super_admin
                              ? "Can edit every rep's clients"
                              : adminGrants.length === 0
                                ? "Cannot edit any sales rep's clients yet"
                                : `Can edit ${adminGrants.length} rep${adminGrants.length === 1 ? "" : "s"}`}
                          </div>
                        </div>
                      </div>

                      {!a.is_super_admin && salesReps.length > 0 && isSuperAdmin && (
                        <EditGrantsManager
                          adminUser={a}
                          salesReps={salesReps}
                          grantedRepIds={adminGrants}
                          onApply={async (repIds, mode) => {
                            await bulkGrant({
                              data: {
                                admin_user_id: a.user_id,
                                rep_user_ids: repIds,
                                mode,
                              },
                            });
                            await grantsQ.refetch();
                          }}
                          onToggleOne={async (repId, granted) => {
                            await setGrant({
                              data: {
                                admin_user_id: a.user_id,
                                rep_user_id: repId,
                                granted,
                              },
                            });
                            await grantsQ.refetch();
                          }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {tab === "audit" && isSuperAdmin && (
          <section>
            <div className="mb-2 flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <History className="h-3.5 w-3.5 text-slate-600" />
                Access Audit Trail
              </h2>
              <span className="text-[11px] text-muted-foreground">
                {(auditQ.data?.entries ?? []).length} Events
              </span>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-2 sm:flex sm:items-end sm:gap-2">
              <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                From
                <Input
                  type="date"
                  value={auditFrom}
                  onChange={(e) => setAuditFrom(e.target.value)}
                  className="h-9"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                To
                <Input
                  type="date"
                  value={auditTo}
                  onChange={(e) => setAuditTo(e.target.value)}
                  className="h-9"
                />
              </label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAuditFrom("");
                  setAuditTo("");
                }}
                className="col-span-2 sm:col-span-1 h-9"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={exportAuditCsv}
                className="col-span-2 sm:col-span-1 h-9 sm:ml-auto"
              >
                Export CSV
              </Button>
            </div>
            {auditQ.isLoading ? (
              <div className="grid place-items-center py-8">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (auditQ.data?.entries ?? []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                <History className="mx-auto mb-2 h-8 w-8 text-muted-foreground/60" />
                <p className="text-sm font-medium">No audit events</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try a wider date range or clear filters.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {(auditQ.data?.entries ?? []).map((e: any) => (
                  <AuditRow key={e.id} entry={e} />
                ))}
              </ul>
            )}
          </section>
        )}
      </main>

    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tint,
}: {
  icon: any;
  label: string;
  value: number;
  tint: string;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-card p-3 sm:p-4 shadow-[0_1px_0_rgba(13,17,38,0.04),0_8px_24px_-16px_rgba(13,17,38,0.18)]"
      style={{ borderColor: "rgba(13,17,38,0.08)" }}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: tint, opacity: 0.9 }}
      />
      <div
        className="grid size-8 sm:size-9 place-items-center rounded-xl"
        style={{ background: `${tint}14`, color: tint }}
      >
        <Icon className="size-4 sm:size-[18px]" strokeWidth={2.2} />
      </div>
      <div className="mt-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground truncate">
        {label}
      </div>
      <div className="mt-0.5 text-xl sm:text-2xl font-extrabold tracking-tight text-foreground tabular-nums">
        {value}
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: any }) {
  const actionMeta: Record<string, { label: string; tint: string; bg: string }> = {
    create: { label: "Created", tint: "#0d6efd", bg: "#0d6efd14" },
    edit: { label: "Edited", tint: "#6f42c1", bg: "#6f42c114" },
    disable: { label: "Disabled", tint: "#dc3545", bg: "#dc354514" },
    enable: { label: "Enabled", tint: "#198754", bg: "#19875414" },
    remove: { label: "Removed", tint: "#6c757d", bg: "#6c757d14" },
    scope_change: { label: "Scope", tint: "#f59e0b", bg: "#f59e0b14" },
  };
  const m = actionMeta[entry.action] ?? actionMeta.edit;
  const when = new Date(entry.created_at).toLocaleString();
  return (
    <li className="rounded-2xl border border-border bg-card p-3 shadow-[0_1px_0_rgba(13,17,38,0.04)]">
      <div className="flex items-start gap-3">
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          style={{ background: m.bg, color: m.tint }}
        >
          {m.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {entry.target_email ?? entry.target_user_id?.slice(0, 8)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
            By {entry.actor_email ?? entry.actor_user_id?.slice(0, 8) ?? "system"} · {when}
          </div>
          {entry.notes && (
            <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
              {entry.notes}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function RepRow({
  rep,
  cities,
  otherReps,
  grantedRepIds,
  canManageGrants,
  onBulkGrant,
  onToggleGrant,
  onChange,
  onRemove,
  onEdit,
  onToggleDisabled,
}: {
  rep: any;
  cities: any[];
  otherReps: any[];
  grantedRepIds: string[];
  canManageGrants: boolean;
  onBulkGrant: (repIds: string[], mode: "replace" | "add" | "remove") => Promise<void>;
  onToggleGrant: (repId: string, granted: boolean) => Promise<void>;
  onChange: (scope: "assigned" | "cities" | "all", ids: string[], can_edit_others: boolean) => void;
  onRemove: () => void;
  onEdit: (display_name: string, password: string) => void | Promise<void>;
  onToggleDisabled: (disabled: boolean) => void | Promise<void>;
}) {
  const [scope, setScope] = useState<"assigned" | "cities" | "all">(rep.scope);
  const [ids, setIds] = useState<string[]>(rep.allowed_city_ids ?? []);
  const [canEditOthers, setCanEditOthers] = useState<boolean>(!!rep.can_edit_others);
  const tone = scopeTone(scope);
  const cityNames = cities
    .filter((c: any) => ids.includes(c.id))
    .map((c: any) => c.name);

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState<string>(rep.display_name ?? "");
  const [editPw, setEditPw] = useState("");
  const [editShowPw, setEditShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState<null | boolean>(null);
  const disabled: boolean = !!rep.is_disabled;

  const pwError = editPw
    ? editPw.length < 8
      ? "Password must be at least 8 characters"
      : !/[A-Za-z]/.test(editPw)
        ? "Password must include a letter"
        : !/[0-9]/.test(editPw)
          ? "Password must include a number"
          : null
    : null;

  return (
    <li
      className={`rounded-xl border bg-card px-3 py-2.5 shadow-[0_1px_0_rgba(13,17,38,0.04)] ${
        disabled ? "border-amber-300/70 opacity-80" : "border-border"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white"
          style={{
            background: disabled
              ? "linear-gradient(135deg,#9ca3af,#6b7280)"
              : "linear-gradient(135deg,#0d6efd,#4f8dff)",
          }}
        >
          {initials(rep.email ?? "?")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="min-w-0 truncate text-sm font-semibold">
              {rep.display_name || rep.email || rep.user_id}
            </div>
            <span
              className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: tone.bg, color: tone.fg }}
            >
              {scopeLabel(scope)}
            </span>
            {disabled && (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                Disabled
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
            {rep.display_name ? rep.email : null}
            {rep.display_name ? " · " : null}
            {scope === "cities" && cityNames.length > 0
              ? cityNames.join(" · ")
              : scope === "all"
                ? "Full database access"
                : "Assigned clients only"}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => {
              setEditName(rep.display_name ?? "");
              setEditPw("");
              setEditShowPw(false);
              setEditOpen(true);
            }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Edit"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setConfirmDisable(!disabled)}
            className={`rounded-md p-1.5 ${
              disabled
                ? "text-emerald-600 hover:bg-emerald-50"
                : "text-amber-600 hover:bg-amber-50"
            }`}
            aria-label={disabled ? "Enable" : "Disable"}
            title={disabled ? "Enable access" : "Disable access"}
          >
            {disabled ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <Ban className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            onClick={onRemove}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            aria-label="Remove"
            title="Remove"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Select
          value={scope}
          onValueChange={(v) => {
            const next = v as "assigned" | "cities" | "all";
            setScope(next);
            const nextEdit = next === "assigned" ? false : canEditOthers;
            if (next === "assigned" && canEditOthers) setCanEditOthers(false);
            onChange(next, next === "cities" ? ids : [], nextEdit);
          }}
        >
          <SelectTrigger className="h-8 w-auto min-w-[170px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="assigned">Assigned Clients Only</SelectItem>
            <SelectItem value="cities">All In Chosen Cities</SelectItem>
            <SelectItem value="all">Full Database Access</SelectItem>
          </SelectContent>
        </Select>

        {scope !== "assigned" && (
          <label className="flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] cursor-pointer hover:bg-muted/40">
            <input
              type="checkbox"
              checked={canEditOthers}
              onChange={(e) => {
                const v = e.target.checked;
                setCanEditOthers(v);
                onChange(scope, scope === "cities" ? ids : [], v);
              }}
              className="h-3.5 w-3.5 accent-primary"
            />
            <span>Edit Others' Clients</span>
          </label>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Rep</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {rep.email} · Leave password blank to keep current
          </p>
          <div className="space-y-2.5 mt-1">
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Display Name
              </label>
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="pl-9"
                  placeholder="Jane Doe"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                New Password
              </label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type={editShowPw ? "text" : "password"}
                  value={editPw}
                  onChange={(e) => setEditPw(e.target.value)}
                  className={`pl-9 pr-9 ${pwError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                  placeholder="Leave blank to keep current"
                  autoComplete="new-password"
                  aria-invalid={!!pwError}
                />
                <button
                  type="button"
                  onClick={() => setEditShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted"
                  aria-label={editShowPw ? "Hide password" : "Show password"}
                >
                  {editShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {pwError ? (
                <p className="mt-1 text-[11px] font-medium text-destructive">{pwError}</p>
              ) : (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  At least 8 characters, with a letter and a number.
                </p>
              )}
            </div>
          </div>
          <Button
            disabled={
              saving ||
              (!editName.trim() && !editPw) ||
              !!pwError
            }
            onClick={async () => {
              setSaving(true);
              try {
                await onEdit(editName.trim(), editPw);
                setEditOpen(false);
                setEditPw("");
              } finally {
                setSaving(false);
              }
            }}
            className="w-full"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDisable !== null}
        onOpenChange={(o) => !o && setConfirmDisable(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmDisable ? "Disable Access?" : "Enable Access?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirmDisable
              ? `${rep.display_name || rep.email} will be signed out and won't be able to log in until re-enabled.`
              : `${rep.display_name || rep.email} will be able to sign in and access the CRM again.`}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setConfirmDisable(null)}>
              Cancel
            </Button>
            <Button
              variant={confirmDisable ? "destructive" : "default"}
              onClick={async () => {
                const v = confirmDisable;
                setConfirmDisable(null);
                if (v !== null) await onToggleDisabled(v);
              }}
            >
              {confirmDisable ? "Disable" : "Enable"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>



      {scope === "cities" && (
        <div className="mt-3 rounded-xl border border-dashed border-border bg-[#f6f7fb] p-2.5">
          <div className="mb-1.5 text-[11px] font-medium text-muted-foreground">
            Allowed cities
          </div>
          <div className="flex flex-wrap gap-1.5">
            {cities.map((c: any) => {
              const on = ids.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => {
                    const next = on ? ids.filter((x) => x !== c.id) : [...ids, c.id];
                    setIds(next);
                    onChange("cities", next, canEditOthers);
                  }}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background hover:bg-muted"
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {canManageGrants && scope !== "assigned" && !canEditOthers && otherReps.length > 0 && (
        <div className="mt-2">
          <EditGrantsManager
            adminUser={rep}
            salesReps={otherReps}
            grantedRepIds={grantedRepIds}
            compact
            onApply={async (repIds, mode) => {
              await onBulkGrant(repIds, mode);
            }}
            onToggleOne={async (repId, granted) => {
              await onToggleGrant(repId, granted);
            }}
          />
        </div>
      )}
    </li>
  );
}

function EditGrantsManager({
  adminUser,
  salesReps,
  grantedRepIds,
  onApply,
  onToggleOne,
  compact = false,
}: {
  adminUser: any;
  salesReps: any[];
  grantedRepIds: string[];
  onApply: (repIds: string[], mode: "replace" | "add" | "remove") => Promise<void>;
  onToggleOne: (repId: string, granted: boolean) => Promise<void>;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<Set<string>>(() => new Set(grantedRepIds));
  const [saving, setSaving] = useState(false);

  // Sync draft when opening or when grants change
  const openDialog = () => {
    setDraft(new Set(grantedRepIds));
    setSearch("");
    setOpen(true);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return salesReps;
    return salesReps.filter((r: any) => {
      const a = (r.display_name ?? "").toLowerCase();
      const b = (r.email ?? "").toLowerCase();
      return a.includes(q) || b.includes(q);
    });
  }, [salesReps, search]);

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setDraft((prev) => {
      const next = new Set(prev);
      filtered.forEach((r: any) => next.add(r.user_id));
      return next;
    });
  };

  const clearAllVisible = () => {
    setDraft((prev) => {
      const next = new Set(prev);
      filtered.forEach((r: any) => next.delete(r.user_id));
      return next;
    });
  };

  const apply = async () => {
    setSaving(true);
    try {
      await onApply(Array.from(draft), "replace");
      toast.success(`Access updated (${draft.size} rep${draft.size === 1 ? "" : "s"})`);
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  };

  const grantAllReps = async () => {
    setSaving(true);
    try {
      await onApply(salesReps.map((r: any) => r.user_id), "replace");
      toast.success("Granted access to all reps");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  };

  const revokeAllReps = async () => {
    setSaving(true);
    try {
      await onApply([], "replace");
      toast.success("Revoked all access");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  };

  const summary =
    grantedRepIds.length === 0
      ? "No reps granted"
      : grantedRepIds.length === salesReps.length
        ? `All ${salesReps.length} reps`
        : `${grantedRepIds.length} of ${salesReps.length} reps`;

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={openDialog}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium hover:bg-muted/40"
        >
          <ShieldCheck className="h-3 w-3 text-emerald-600" />
          <span className="text-muted-foreground">Edit Grants:</span>
          <span className="text-foreground">{summary}</span>
        </button>
      ) : (
        <div
          className="mt-3 rounded-xl border bg-muted/30 p-2.5"
          style={{ borderColor: "rgba(13,17,38,0.06)" }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Edit Access To Rep Data
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground truncate">{summary}</div>
            </div>
            <Button size="sm" variant="outline" onClick={openDialog} className="h-8 shrink-0">
              Manage
            </Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Edit Access · {adminUser.display_name || adminUser.email}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={grantAllReps} disabled={saving}>
              Grant All
            </Button>
            <Button size="sm" variant="outline" onClick={revokeAllReps} disabled={saving}>
              Revoke All
            </Button>
            <span className="ml-auto self-center text-[11px] text-muted-foreground">
              {draft.size} Selected
            </span>
          </div>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search reps by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 pl-9"
            />
          </div>

          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              Showing {filtered.length} of {salesReps.length}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllVisible}
                className="font-semibold text-primary hover:underline"
              >
                Select Visible
              </button>
              <span>·</span>
              <button
                type="button"
                onClick={clearAllVisible}
                className="font-semibold text-primary hover:underline"
              >
                Clear Visible
              </button>
            </div>
          </div>

          <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-border bg-card">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No reps match "{search}"
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((rep: any) => {
                  const checked = draft.has(rep.user_id);
                  const label = rep.display_name || rep.email || rep.user_id.slice(0, 8);
                  return (
                    <li key={rep.user_id}>
                      <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/40">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(rep.user_id)}
                          className="h-4 w-4 rounded border-border accent-emerald-600"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{label}</div>
                          {rep.display_name && rep.email && (
                            <div className="truncate text-[11px] text-muted-foreground">
                              {rep.email}
                            </div>
                          )}
                        </div>
                        {checked && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={apply} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>

  );
}
